/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/subscriptionSettings.scss';

import React, {Fragment, MouseEventHandler, useMemo, useState} from 'react';
import Subscription from "../../../models/subscriptionModel";
import classNames from 'classnames';
import {DiscountType, SubscriptionStatus} from "../../../util/interfaces";
import {AwesomeIcon, ElementPosition, IndexedObject, Loader, OrnamentShape, parseMoney} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {BolmateButton} from "../../controls";
import Image from "../../misc/image";
import Ideal from '../../misc/ideal';
import Sepa from '../../misc/sepa';
import Mastercard from '../../misc/mastercard';
import NotificationsModuleController from "./modules/notificationsModule/notificationsModuleController";

interface Props {
    onClickPortal: MouseEventHandler;
    onClickStart: MouseEventHandler;
    onCancelSubscription: MouseEventHandler;
    onClickCoupon: MouseEventHandler;
    onClickFailedPayment: MouseEventHandler;
    onOpenCustomerDetails: MouseEventHandler;
    currentUsage: number;
    currentPrice: number;
    currentPeriodFrom: IDate | null;
    currentPeriodTo: IDate | null;
    subscription: Subscription;
    loadingSubscription: boolean;
    previousPeriods: IndexedObject<number>;
    usageLastMonth: number;
    discountPrice: number | null;
    extraAccounts: number;
    activeUsersEstimate: number;
}

type SubscriptionAction = {label: string; onClick: MouseEventHandler;};

export default function SubscriptionSettings(props: Props) {
    const {
        subscription, loadingSubscription, onCancelSubscription, currentPeriodFrom, currentPeriodTo, currentPrice,
        currentUsage, usageLastMonth, previousPeriods, onClickStart, onClickPortal, onClickCoupon, discountPrice,
        extraAccounts, onClickFailedPayment, activeUsersEstimate, onOpenCustomerDetails
    } = props;
    const [previousOpen, setPreviousOpen] = useState(false);
    const actions = useMemo(() => {
        const {status, failedPayment, paymentMethods} = subscription;
        if (status === SubscriptionStatus.FREE) return [];
        const actions: Array<SubscriptionAction> = [
            {label: 'Gegevens wijzigen', onClick: onOpenCustomerDetails},
            {label: 'Facturen bekijken', onClick: onClickPortal},
            {label: 'Coupon toepassen', onClick: onClickCoupon},
        ];
        if (failedPayment?.paymentUrl.length) actions.splice(0, 0, {
            label: 'Openstaande factuur betalen', onClick: onClickFailedPayment
        });
        else if (failedPayment) actions.splice(0, 0, {
            label: 'Openstaande factuur betalen', onClick: onClickFailedPayment
        });
        else if (status === SubscriptionStatus.INACTIVE || !paymentMethods.length) return actions.concat([
            {label: 'Abonnement starten', onClick: onClickStart}
        ]);
        else {
            const startStopAction = status === SubscriptionStatus.CANCELED ? onClickStart : onCancelSubscription;
            const startStopLabel = status === SubscriptionStatus.CANCELED ? 'starten' : 'stopzetten';
            actions.push(
                {label: `Abonnement ${startStopLabel}`, onClick: startStopAction},
                {label: 'Betaalwijze aanpassen', onClick: onClickStart},
            )
        }
        return actions;
    }, [subscription.status, subscription.paymentMethods, subscription.failedPayment]);

    function onOpenFeature(feature: 'features' | 'prices') {
        return function() {
            if (feature === 'features') return window.open('https://bolmate.nl/#features', '_blank');
            else window.open('https://bolmate.nl/#pricing', '_blank')
        }
    }

    function onClickPreviousPeriods() {
        setPreviousOpen(!previousOpen);
    }

    function renderInvoiceCount() {
        return (
            <div className='block invoice-count'>
                {renderSmallLoader()}
                <label className='block-title'>Aantal orders</label>
                <label className='block-sub'>afgelopen 30 dagen</label>
                <label className='count'>{usageLastMonth}</label>
            </div>
        );
    }

    function renderSmallLoader() {
        return <Loader active={loadingSubscription} size='2.5rem' thickness='.1rem'/>
    }

    function renderStatusBlock() {
        const {status, isTrialing, hasPaymentMethod, paymentMethods} = subscription;
        const paymentMethod = hasPaymentMethod ? paymentMethods[0] : null;
        let statusClass: string;
        if (isTrialing && ![SubscriptionStatus.ACTIVE, SubscriptionStatus.CANCELED].includes(
            status)) statusClass = 'trialing';
        else statusClass = status;
        return (
            <div className='block status'>
                {renderSmallLoader()}
                <label className='block-title'>Status</label>
                <label className='block-sub'>huidig abonnement</label>
                <div className='row'>
                    <label
                        className={classNames('sub-status', statusClass)}>{subscription.statusLabel}</label>
                    {renderDiscountLabel()}
                </div>
                {paymentMethod !== null && (
                    <div className='payment-details'>
                        <label>Betaalmethode:&nbsp;</label>
                        <label>{paymentMethod.displayPaymentType}</label>
                        <label>Rekeningnummer:&nbsp;</label>
                        <label>{paymentMethod.bank}<span className='stars'>****</span>{paymentMethod.accountNumber}
                        </label>
                    </div>
                )}
                {(isTrialing && subscription.trialEndDate) &&
                    <label
                        className='trial-end-date'>{renderStatusTrialText(status, subscription.trialEndDate)}</label>}
                {renderDiscountDuration()}
            </div>
        );
    }

    function renderDiscountLabel() {
        const {discount, discountType} = subscription;
        if (!subscription.hasActiveDiscount()) return null;
        if (discountType === DiscountType.PERCENT) return <label className='sub-status blue'>{discount}%
            korting</label>;
        else return <label className='sub-status blue sm'>&euro;{parseMoney(discount)} korting</label>;
    }

    function renderStatusTrialText(status: SubscriptionStatus, trialEndDate: IDate) {
        if (status === SubscriptionStatus.ACTIVE) return `Proef nog tot ${trialEndDate.format(
            'DD-MM-YYYY')}, daarna betaald`
        else return `tot ${trialEndDate.format('DD-MM-YYYY')}`
    }

    function renderDiscountDuration() {
        const {discountUntil} = subscription;
        if (!discountUntil) return null;
        return <label className='trial-end-date'>Korting geldig tot {discountUntil.format('DD-MM-YYYY')}</label>
    }

    function renderActionsBlock() {
        return (
            <div className='block actions'>
                {renderSmallLoader()}
                <label className='block-title'>Huidig abonnement</label>
                {renderActionsContent()}
            </div>
        );
    }

    function renderActionsContent() {
        if (!actions.length) return null;
        return actions.map((action: SubscriptionAction, index: number) => (
            <label key={index} className='action' onClick={action.onClick}>{action.label}</label>
        ));
    }

    function renderCurrentDetails() {
        const {status, hasPaymentMethod, failedPayment} = subscription;
        let btnAction: MouseEventHandler, btnLabel: string, ornament: OrnamentShape | undefined = undefined;
        if (failedPayment) {
            btnAction = onClickFailedPayment;
            btnLabel = 'Openstaande factuur betalen';
        } else if (status === SubscriptionStatus.CANCELED) {
            btnAction = onClickStart;
            btnLabel = 'Abonnement starten';
            ornament = {element: <AwesomeIcon name='arrow-right'/>, placement: ElementPosition.RIGHT}
        } else if (status === SubscriptionStatus.INACTIVE || !hasPaymentMethod) {
            btnAction = onClickStart;
            btnLabel = 'Abonnement starten';
            ornament = {element: <AwesomeIcon name='arrow-right'/>, placement: ElementPosition.RIGHT}
        } else {
            btnAction = onClickPortal;
            btnLabel = 'Abonnement wijzigen';
        }
        let usageLabel = `Prijs bij ${usageLastMonth} orders per maand`;
        if (extraAccounts > 0) usageLabel += ` + ${extraAccounts} extra account${extraAccounts === 1 ? '' : 's'}`;
        return (
            <div className='subscription-block current-details'>
                <Loader active={loadingSubscription}/>
                <div className='header-container'>
                    <Image name='logo-no-text' className='logo'/>
                    <label className='header-title'>Bolmate - Reviews, facturen en mailing in één</label>
                </div>
                <div className='body-container'>
                    <label className='subscription-block-title'>{usageLabel}</label>
                    <label className='price end'>{renderCurrentPrice()}</label>
                    <div className='disclaimer-container'>
                        <span>*</span>&nbsp;<label>Prijs wordt maandelijks bepaald aan de hand van het aantal
                        orders.<br/>Zo betaal je nooit teveel.<br/>Genoemde prijzen zijn excl. 21% BTW.</label>
                    </div>
                    <div className='checkout-signals-container'>
                        <div className='checkout-signals'>
                            <label className='end'>Meer dan <b>{activeUsersEstimate}</b> actieve gebruikers</label>
                            <AwesomeIcon name='check' prefix='fas' className='check-icon'/>
                            <label className='end'><b>4.9</b> uit 5 sterren op Google reviews</label>
                            <AwesomeIcon name='check' prefix='fas' className='check-icon'/>
                            <label className='end'><b>Miljoenen</b> succesvolle facturen verstuurd!</label>
                            <AwesomeIcon name='check' prefix='fas' className='check-icon'/>
                        </div>
                        {(status !== SubscriptionStatus.ACTIVE) && <div className='payment-icons'>
                            <Ideal width='2.32rem' height='2.32rem'/>
                            <Sepa width='2rem' height='2rem'/>
                            <Mastercard width='3rem' height='3rem'/>
                        </div>}
                    </div>
                    {status !== SubscriptionStatus.FREE ?
                        <BolmateButton onClick={btnAction} label={btnLabel}
                                       secondary={status === SubscriptionStatus.ACTIVE}
                                       className='subscription-btn end' ornaments={ornament}/> : <div/>}
                    <div className='feature-labels'>
                        {renderFeatureLabel('Alle features bekijken', 'features')}
                        {renderFeatureLabel('Alle prijzen bekijken', 'prices')}
                    </div>

                </div>
            </div>
        )
    }

    function renderCurrentPrice() {
        if (discountPrice === null)
            return `\u20ac${parseMoney(currentPrice)}`;
        else
            return <><span
                className='strike'>&euro; {parseMoney(currentPrice)}</span> <span
                className='discount-price'>&euro;{parseMoney(discountPrice)}</span></>;
    }

    function renderFeatureLabel(label: string, feature: 'features' | 'prices') {
        return (
            <label className='feature-label' onClick={onOpenFeature(feature)}>{label} <AwesomeIcon name='caret-right'
                                                                                                   className='feature-arrow'/>
            </label>
        );
    }

    function renderCurrentUsage() {
        return (
            <div className='subscription-block current-usage'>
                <Loader active={loadingSubscription}/>
                <div className='header-container'>
                    <label className='header-title'>Verbruik deze periode</label>
                    <label className='sub-title'>Sinds {currentPeriodFrom?.format('DD-MM-YYYY') ?? '-'} tot
                        vandaag</label>
                </div>
                <div className='body-container'>
                    <label className='bold'>Aantal orders</label>
                    <label className='bold'>Deze periode</label>
                    <label/>
                    <label>{currentUsage}</label>
                    <label>{currentPeriodFrom?.format('DD-MM-YYYY') ?? '-'} t/m {currentPeriodTo?.format(
                        'DD-MM-YYYY') ?? '-'}</label>
                    <label className='action'
                           onClick={onClickPreviousPeriods}>{previousOpen ? 'Verberg' : 'Bekijk'} verbruik vorige
                        periodes</label>
                    {renderPreviousPeriods()}
                </div>
            </div>
        )
    }

    function renderPreviousPeriods() {
        if (!previousOpen) return null;
        return Object.keys(previousPeriods).map((period: string, index: number) => (
            <Fragment key={index}>
                <label>{previousPeriods[period]}</label>
                <label>{period}</label>
                <label/>
            </Fragment>
        ));
    }

    return (
        <div className='subscription-settings'>
            <div className='blocks-container'>
                {renderInvoiceCount()}
                {renderStatusBlock()}
                {renderActionsBlock()}
            </div>
            {renderCurrentDetails()}
            <NotificationsModuleController loading={loadingSubscription} usageLastMonth={usageLastMonth}/>
            {renderCurrentUsage()}
        </div>
    )
}